import ErrorIllustration from 'assets/svg/error/error-illustration-big.svg'
import Image from 'common/components/Image'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import { useStoreActions, useStoreState } from 'easy-peasy'
import { useCallback, useEffect, useState } from 'react'
import { CategoryType } from 'services/Category/models/CategoryModel/inferface'
import ICategoryModel from 'stores/models/interface'
import styled from 'styled-components'

const CategoryContainer = styled.div`
  width: 100%;
  max-height: 500px;
  height: 500px;
  display: flex;
`
const MainCategory = styled.div`
  flex: 0 1 auto;
  padding: 24px 10px;
  min-width: 280px;
  overflow-y: auto;
`
const SubCategory = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  border-left: 1px solid ${theme.colors.grayLight};
  border-right: 1px solid ${theme.colors.grayLight};
`
const BannerCategory = styled.div`
  flex: 0 1 auto;
  padding: 24px 20px;
  width: 298px;
  min-width: 280px;
`
const MainCategoryHeader = styled.div`
  margin-left: 14px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`
const SubCategoryHeader = styled.div`
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: ${theme.colors.white};
  padding: 24px;
`
const SubCategoryBody = styled.div`
  flex: 1 1 0%;
  padding: 0 24px 24px 24px;
`
const SubCategoryColumn = styled.div`
  column-count: 3;
  column-gap: 24px;
`
const MainCategoryItem = styled.a`
  color: ${theme.colors.black};
  margin: 2px;
  padding: 4px 10px;
  display: block;
  cursor: pointer;

  &:hover {
    border-radius: 6px;
    color: ${theme.colors.orange};
    background-color: ${theme.colors.grayLighter};
    font-weight: 600;
  }

  ${({ active }) =>
    active
      ? `
        border-radius: 6px;
        color: ${theme.colors.orange};
        background-color: ${theme.colors.grayLighter};
        font-weight: 600;
      `
      : ''}
`
const MainCategoryItemText = styled.span`
  margin-left: 16px;
  font-size: 13px;
`
const SubCategoryItem = styled.div`
  width: 100%;
  vertical-align: top;
  display: inline-block;
  margin-bottom: 30px;
`
const SubCategoryItemLink = styled.a`
  color: ${theme.colors.black};
  display: block;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;

  &:hover {
    color: ${theme.colors.orange};
  }
`

const ChildrenSubCategoryItemLink = styled.a`
  color: ${theme.colors.black};
  display: block;
  cursor: pointer;
  font-size: 13px;
  margin-bottom: 7px;

  &:hover {
    color: ${theme.colors.orange};
  }
`

const StyledSkeleton = styled(Skeleton)`
  margin: ${({ margin }) => margin};
  margin-bottom: 12px;
  display: inline-block;
`
const ErrorView = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`
const TryAgain = styled.span`
  color: ${theme.colors.orange};
  cursor: pointer;
`

function Category(): JSX.Element {
  const skeletonCount = useCallback((x) => new Array(x).fill(0), [])
  const [currentCategory, setCurrentCategory] = useState<
    CategoryType
  >()

  const {
    categories,
    isLoading,
    isError,
    isAlreadyGetCategories,
  } = useStoreState<ICategoryModel>((state) => state.category)
  const { getCategory, reset } = useStoreActions<ICategoryModel>(
    (actions) => actions.category,
  )

  useEffect(() => {
    if (!isAlreadyGetCategories) {
      getCategory()
    }
  }, [isAlreadyGetCategories])

  useEffect(() => {
    setCurrentCategory(categories?.[0])
  }, [categories])

  return (
    <CategoryContainer data-testid="category-container">
      {!isError && (
        <MainCategory>
          <MainCategoryHeader>
            <Typography type="bh6">Kategori</Typography>
            <a href={`${WEB_DESKTOP_RALALI_URL}/all-categories`}>
              <Typography color="orange">Lihat Semua</Typography>
            </a>
          </MainCategoryHeader>
          {isLoading &&
            skeletonCount(8).map((_, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <StyledSkeleton key={idx} height="32px" />
            ))}
          {!isLoading &&
            categories?.map((main) => (
              <MainCategoryItem
                key={main.id}
                onMouseOver={() => setCurrentCategory(main)}
                active={main.id === currentCategory?.id}
                data-testid="main-category"
                href={`${WEB_DESKTOP_RALALI_URL}/all-categories/${main.alias}`}
              >
                <Image
                  width="25px"
                  height="25px"
                  src={main.menu_icon}
                />
                <MainCategoryItemText>
                  {main.name}
                </MainCategoryItemText>
              </MainCategoryItem>
            ))}
        </MainCategory>
      )}
      {!isError && (
        <SubCategory>
          {isLoading && (
            <StyledSkeleton
              margin="24px"
              height="32px"
              width="320px"
            />
          )}
          {!isLoading && (
            <SubCategoryHeader>
              <Image
                width="24px"
                height="24px"
                src={currentCategory?.menu_icon}
              />
              <Typography type="bh6" marginLeft="10px">
                {currentCategory?.name}
              </Typography>
            </SubCategoryHeader>
          )}
          <SubCategoryBody>
            <SubCategoryColumn>
              {isLoading &&
                skeletonCount(10).map((_, idx) => (
                  <StyledSkeleton
                    // eslint-disable-next-line react/no-array-index-key
                    key={`skeleton-${idx}`}
                    height="20px"
                    width="120px"
                  />
                ))}
              {!isLoading &&
                currentCategory?.children?.map((sub) => (
                  <SubCategoryItem key={sub?.id}>
                    <SubCategoryItemLink
                      href={`${WEB_DESKTOP_RALALI_URL}/c/${sub.alias}`}
                    >
                      {`${sub?.name} (${sub?.children.length})`}
                    </SubCategoryItemLink>
                    {sub?.children.map((children) => (
                      <ChildrenSubCategoryItemLink
                        key={children.id}
                        href={`${WEB_DESKTOP_RALALI_URL}/c/${children.alias}`}
                      >
                        {children?.name}
                      </ChildrenSubCategoryItemLink>
                    ))}
                  </SubCategoryItem>
                ))}
            </SubCategoryColumn>
          </SubCategoryBody>
        </SubCategory>
      )}
      {!isError && (
        <BannerCategory>
          {!isLoading && (
            <Image
              objectFit="cover"
              radius="6px"
              width="100%"
              height="100%"
              src={currentCategory?.menu_banner?.path_image || '/'}
            />
          )}
        </BannerCategory>
      )}
      {isError && (
        <ErrorView>
          <ErrorIllustration />
          <Typography textAlign="center" marginTop="20px">
            <>
              Mohon maaf terjadi kesalahan. Silahkan coba beberapa
              saat lagi atau&nbsp;
              <TryAgain
                data-testid="try-again-test"
                onClick={() => {
                  reset()
                  getCategory()
                }}
              >
                coba lagi
              </TryAgain>
            </>
          </Typography>
        </ErrorView>
      )}
    </CategoryContainer>
  )
}

export default Category
